import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Get the current user
 * @returns {Object|null} - Current user or null
 */
export const getCurrentUser = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    
    if (!data?.session?.user) {
      console.warn('No active user session found');
      return null;
    }
    
    console.log('Current user:', data.session.user.id);
    return data.session.user;
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    return null;
  }
};

/**
 * Save a project to Supabase
 * @param {Object} projectData - Project data to save
 * @returns {Promise<Object>} - Saved project data
 */
export const saveProject = async (projectData) => {
  try {
    // Check if user is authenticated
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    console.log('ENTRY DEBUG - saveProject called with projectData:', projectData);
    console.log('ENTRY DEBUG - Entry points being saved:', projectData.entryPoints);
    
    // Get entry points from either field for backward compatibility
    const entryPoints = projectData.entryPoints || projectData.entry_points || [];
    
    // Check if project exists
    if (projectData.id) {
      // Update existing project
      console.log('ENTRY DEBUG - Updating existing project:', projectData.id);
      const { data, error } = await supabase
        .from('projects')
        .update({
          name: projectData.name || 'Untitled Project',
          image_url: projectData.imageUrl,
          boxes: projectData.boxes || [],
          shapes: projectData.shapes || [],
          entry_points: entryPoints,
          scale: projectData.scale || { pixelsPerFoot: 30, referenceLine: null },
          ceiling_height: projectData.ceilingHeight || 9.0,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectData.id)
        .select();
      
      if (error) throw error;
      
      // Add entryPoints field to response for client-side consistency
      const updatedData = {
        ...data[0],
        entryPoints: data[0].entry_points
      };
      
      console.log('ENTRY DEBUG - Project updated successfully. Response data:', updatedData);
      return updatedData;
    } else {
      // Create new project
      console.log('ENTRY DEBUG - Creating new project');
      const { data, error } = await supabase
        .from('projects')
        .insert([{
          user_id: user.id, // Keep for backward compatibility 
          created_by: user.id, // New field to track creator
          name: projectData.name || 'Untitled Project',
          image_url: projectData.imageUrl,
          boxes: projectData.boxes || [],
          shapes: projectData.shapes || [],
          entry_points: entryPoints,
          scale: projectData.scale || { pixelsPerFoot: 30, referenceLine: null },
          ceiling_height: projectData.ceilingHeight || 9.0
        }])
        .select();
      
      if (error) throw error;
      
      // Add entryPoints field to response for client-side consistency
      const newData = {
        ...data[0],
        entryPoints: data[0].entry_points
      };
      
      console.log('ENTRY DEBUG - New project created successfully. Response data:', newData);
      return newData;
    }
  } catch (error) {
    console.error('Error saving project:', error);
    throw error;
  }
};

/**
 * Get all projects for all users with minimal data (for list view)
 * @returns {Promise<Array>} - Array of projects with minimal data
 */
export const getProjects = async () => {
  try {
    const user = await getCurrentUser();
    if (!user) return [];

    // Get all projects with minimal data for faster loading
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, image_url, customer, location, archived, created_at, updated_at, user_id')
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
};

/**
 * Get a single project by ID with full data
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Full project data including JSON fields
 */
export const getProject = async (projectId) => {
  try {
    const user = await getCurrentUser();
    if (!user) return null;

    console.log('ENTRY DEBUG - getProject called for projectId:', projectId);
    
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();
    
    if (error) throw error;
    
    console.log('ENTRY DEBUG - Project retrieved:', data);
    console.log('ENTRY DEBUG - Entry points in retrieved project:', data.entry_points);
    
    // Add entryPoints field for client-side consistency
    const projectData = {
      ...data,
      entryPoints: data.entry_points || []
    };
    
    return projectData;
  } catch (error) {
    console.error('Error fetching project:', error);
    return null;
  }
};

/**
 * Delete a project
 * @param {string} projectId - Project ID
 * @returns {Promise<boolean>} - Success status
 */
export const deleteProject = async (projectId) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

/**
 * Update a project
 * @param {string} projectId - Project ID
 * @param {Object} projectData - Project data to update
 * @returns {Promise<Object>} - Updated project data
 */
export const updateProject = async (projectId, projectData) => {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // Get entry points from either field for backward compatibility
    const entryPoints = projectData.entryPoints || projectData.entry_points || [];
    
    const { data, error } = await supabase
      .from('projects')
      .update({
        name: projectData.name,
        boxes: projectData.boxes,
        shapes: projectData.shapes,
        entry_points: entryPoints,
        scale: projectData.scale,
        ceiling_height: projectData.ceilingHeight,
        updated_at: new Date().toISOString()
      })
      .eq('id', projectId)
      .select();
    
    if (error) throw error;
    
    // Add entryPoints field to response for client-side consistency
    const updatedData = {
      ...data[0],
      entryPoints: data[0].entry_points
    };
    
    return updatedData;
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

/**
 * Get the pricing preset
 * @returns {Promise<Object>} - Pricing preset data
 */
export const getPricingPreset = async () => {
  try {
    const { data, error } = await supabase
      .from('pricing_presets')
      .select('*')
      .single();
    
    if (error) {
      // Handle table not exists error gracefully
      if (error.code === '42P01') {
        console.error('pricing_presets table does not exist:', error);
        console.warn('You need to run the database migration to create the pricing_presets table');
        return null;
      }
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Error fetching pricing preset:', error);
    return null;
  }
};

/**
 * Update the pricing preset
 * @param {string} presetId - Preset ID
 * @param {Object} settings - Updated settings
 * @returns {Promise<Object>} - Updated preset data
 */
export const updatePricingPreset = async (presetId, settings) => {
  try {
    // Ensure user has permission to update presets
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');
    
    // In the future, add permission check here
    
    const { data, error } = await supabase
      .from('pricing_presets')
      .update({ 
        settings,
        updated_at: new Date().toISOString()
      })
      .eq('id', presetId)
      .select();
    
    if (error) throw error;
    return data[0];
  } catch (error) {
    console.error('Error updating pricing preset:', error);
    throw error;
  }
};

/**
 * Update lock status for a specific pricing item
 * @param {string} presetId - Preset ID
 * @param {string} section - Section name (hardware, software, etc.)
 * @param {string} item - Item name (entry, oa, etc.)
 * @param {boolean} locked - New lock status
 * @returns {Promise<Object>} - Updated preset data
 */
export const updatePricingItemLock = async (presetId, section, item, locked) => {
  try {
    // First get the current preset
    const preset = await getPricingPreset();
    if (!preset) throw new Error('Preset not found');
    
    // Create a deep copy of the settings
    const settings = JSON.parse(JSON.stringify(preset.settings));
    
    // Update the lock status
    if (settings[section] && settings[section][item]) {
      settings[section][item].locked = locked;
    } else {
      throw new Error(`Item ${section}.${item} not found in preset`);
    }
    
    // Update the preset
    return await updatePricingPreset(presetId, settings);
  } catch (error) {
    console.error('Error updating pricing item lock:', error);
    throw error;
  }
}; 